import React, { useEffect, useState } from "react";
import { ProColumns } from "@ant-design/pro-table/es/typing";
import { Button, Modal, Tag } from "antd";
import {
  VideoCameraOutlined,
  AudioOutlined,
  DeleteOutlined,
  FileOutlined,
  PlusCircleOutlined,
  PlayCircleOutlined,
  CheckCircleFilled,
  MinusOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import Table from "../../../Common/Table";
import clsx from "clsx";
import dayjs from "dayjs";
import RecordingModal from "./RecordingModal";
import {
  RecordingGetParams,
  RecordingTableRow,
  useRecordingTableGet,
  useRecordingVaadTableGet,
} from "../../../../hooks/api/recordings";
import { SortOrder } from "antd/es/table/interface";
import { RequestData } from "@ant-design/pro-components";
import { getSorterParams, getUserRequestPath } from "../../../../utils";
import { useSearchParams } from "react-router";
import DeleteRecordingButton from "./actions";
import { IconAudio, IconVideo } from "../../../Common/Icon";
import { downloadFile } from "../../../../hooks/file";
import { useMiddahTableGet } from "../../../../hooks/api/middot";
import { LazySelect } from "../../../Common/SelectLazyLoader";
import VideoPlayerModal from "../../../Common/VideoPlayerModal";
import { useAuth, UserRoleType } from "../../../../store/auth";

const TAG_COLORS: Record<
  string,
  { bg: string; border: string; color: string }
> = {
  simcha: { bg: "#f0f5ff", border: "#d6e4ff", color: "#2f54eb" },
  "chapter 1": { bg: "#f0f5ff", border: "#d6e4ff", color: "#2f54eb" },
  introduction: { bg: "#f0f5ff", border: "#d6e4ff", color: "#2f54eb" },
};

const getTagStyle = (tag: string) => {
  const normalized = tag.toLowerCase();
  return (
    TAG_COLORS[normalized] || {
      bg: "#f5f5f5",
      border: "#d9d9d9",
      color: "#666",
    }
  );
};

const TYPE_COLORS: Record<string, string> = {
  video: "#1677FF",
  audio: "#52C41A",
};

function ToolBar({ onCreate }: { onCreate: () => void }) {
  const { user } = useAuth();
  const isAdmin = user?.role
    ? getUserRequestPath(user.role as UserRoleType) === "admin"
    : false;

  return isAdmin ? (
    <>
      <Button
        key="create"
        icon={<PlusCircleOutlined />}
        type="primary"
        onClick={(e) => {
          e.preventDefault();
          onCreate();
        }}
      >
        Upload Recordings
      </Button>
    </>
  ) : null;
}

export default function RecordingsTab(): React.ReactNode | null {
  const { user } = useAuth();
  const [, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(() => Date.now());
  const { t, i18n } = useTranslation();
  const recordingTableGet = useRecordingTableGet();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [editingRecord, setEditingRecord] = useState<RecordingTableRow | null>(
    null,
  );
  const [videoPlayerOpen, setVideoPlayerOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState<{
    name: string;
    fileLink: string;
  } | null>(null);
  const middahTableGet = useMiddahTableGet();
  const vaadTableGet = useRecordingVaadTableGet();
  const isAdmin = user?.role
    ? getUserRequestPath(user.role as UserRoleType) === "admin"
    : false;

  const fetchMiddahOptions = async (params: {
    search?: string;
    page: number;
    pageSize: number;
  }) => {
    const response = await middahTableGet.fetch({
      page: params.page,
      pageSize: params.pageSize,
      search: params.search,
    });
    return response?.data || [];
  };

  const mapMiddahOption = (item: any) => ({
    label: item.name,
    value: item.id,
  });

  const fetchVaadOptions = async (params: {
    search?: string;
    page: number;
    pageSize: number;
  }) => {
    const response = await vaadTableGet.fetch({
      page: params.page,
      pageSize: params.pageSize,
      search: params.search,
    });
    return response?.data || [];
  };

  const mapVaadOption = (item: any) => ({
    label: item.name,
    value: item.id,
  });

  const openVideoPlayer = (record: RecordingTableRow) => {
    if (record.fileLink) {
      setCurrentVideo({
        name: record.name,
        fileLink: record.fileLink,
      });
      setVideoPlayerOpen(true);
    }
  };
  useEffect(() => {
    setTableKey(Date.now());
  }, []);

  const handleCreate = () => {
    setModalMode("create");
    setEditingRecord(null);
    setModalOpen(true);
  };

  const handleEdit = (record: RecordingTableRow) => {
    setModalMode("edit");
    setEditingRecord(record);
    setModalOpen(true);
  };

  const handleSave = () => {
    setSearchParams({}, { replace: true });
    setTableKey(Date.now());
    setModalOpen(false);
  };

  const columns: ProColumns<RecordingTableRow>[] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      hideInSearch: true,
      render: (_, record) => (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: record.fileLink ? "pointer" : "default",
          }}
          onClick={() => record.fileLink && openVideoPlayer(record)}
        >
          {record.type === "video" ? <IconVideo /> : <IconAudio />}
          <span
            style={{
              textDecoration: record.fileLink ? "underline" : "none",
            }}
          >
            {record.name}
          </span>
        </span>
      ),
    },
    {
      title: "Vaad Name",
      dataIndex: "vaadName",
      key: "vaadName",
      hideInSearch: true,
      render: (_, record) => record.vaad?.name || "-",
    },
    {
      title: "Vaad Name",
      dataIndex: "vaadId",
      key: "vaadId",
      valueType: "select",
      hideInTable: true,
      renderFormItem: (_schema, _config, form) => (
        <LazySelect
          fetchOptions={fetchVaadOptions}
          mapOption={mapVaadOption}
          pageSize={20}
          onClear={() => form?.submit()}
        />
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      sorter: true,
      valueType: "select",
      valueEnum: {
        video: { text: "Video" },
        audio: { text: "Audio" },
      },
      // hideInSearch: true,
      render: (_, record) => (
        <Tag
          color={TYPE_COLORS[record.type.toLowerCase()] || "default"}
          variant="outlined"
          style={{ textTransform: "capitalize", fontSize: 12 }}
        >
          {record.type}
        </Tag>
      ),
    },
    {
      title: "Tags",
      dataIndex: "tags",
      key: "tags",
      valueType: "select",
      hideInSearch: true,
      render: (_, record) => {
        const allTags = record.middahs || [];
        const visibleTags = allTags.slice(0, 3);
        const hiddenCount = allTags.length - 3;

        return (
          <span
            style={{
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {visibleTags.map((tag) => (
              <Tag color="#722ED1" variant="outlined" key={tag.id}>
                {tag.name}
              </Tag>
            ))}
            {hiddenCount > 0 && <span>+{hiddenCount}</span>}
          </span>
        );
      },
    },
    {
      title: "Tags",
      dataIndex: "middahIds",
      key: "middahIds",
      valueType: "select",
      hideInTable: true,
      renderFormItem: () => (
        <LazySelect
          mode="multiple"
          fetchOptions={fetchMiddahOptions}
          mapOption={mapMiddahOption}
          pageSize={20}
        />
      ),
    },
    {
      title: "Global",
      dataIndex: "globalContent",
      key: "globalContent",
      align: "center",
      hideInSearch: true,
      render: (_, record) =>
        record.globalContent ? (
          <CheckCircleFilled style={{ color: "#52C41A", fontSize: 16 }} />
        ) : (
          <MinusOutlined style={{ color: "#BFBFBF" }} />
        ),
    },
    {
      title: "Upload Date",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      hideInSearch: true,
      render: (_, record) => {
        const date = dayjs(record.createdAt);
        return date.isValid() ? date.format("MMMM D, YYYY") : "-";
      },
    },
    {
      title: "Upload Date",
      dataIndex: "createdAt",
      key: "createdAt",
      // sorter: true,
      hideInTable: true,
      // hideInSearch: true,
      valueType: "dateRange",
      fieldProps: { format: "MM-DD-YYYY" },
      search: {
        transform: (value?: [string, string]) => {
          if (!value || value.length !== 2) return {};

          return {
            createdAtFrom: value[0],
            createdAtTo: value[1],
          };
        },
      },
    },
  ];
  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number;
      current?: number;
      keyword?: string;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<RecordingTableRow>>> => {
    const newParams: RecordingGetParams = {
      page: current || 1,
      pageSize: pageSize || 10,
      orderByColumn: "createdAt",
      orderBy: "DESC",
      ...args,
      ...getSorterParams(sorter),
    };

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });

    return recordingTableGet.fetch(newParams).then((response) => {
      if (response) {
        const { data, total } = response;

        return {
          data: data || [],
          success: true,
          total,
        };
      }

      return { data: [], success: false, total: 0 };
    });
  };

  const actions: any[] = [
    [
      "download",
      ({ record }: { record: RecordingTableRow }) => ({
        title: "Download",
        onClick: (e: MouseEvent) => {
          e.stopPropagation();
          downloadFile(record.fileLink, record.name);
        },
      }),
    ],
    ...(isAdmin
      ? [
        [
          "edit",
          ({ record }: { record: RecordingTableRow }) => ({
            title: "Edit",
            onClick: (e: MouseEvent) => {
              e.stopPropagation();
              handleEdit(record);
            },
          }),
        ],
        [
          DeleteRecordingButton,
          {
            setTableKey,
          },
        ],
      ]
      : []),
  ];

  return (
    <>
      <Table<RecordingTableRow>
        key={`recordings-${tableKey}`}
        resetKey={() => setTableKey(Date.now())}
        // className={clsx("transparent")}
        className="tableCustomPadding"
        headerTitle={t("pages.recordings.title")}
        columns={columns}
        request={tableRequest}
        onReset={() => {
          setSearchParams({}, { replace: true });
          setTableKey(Date.now());
        }}
        toolBarRender={() => [
          <ToolBar key="toolbar" onCreate={() => handleCreate()} />,
        ]}
        actions={actions}
        search={{
          style: {
            // display: "none",
            border: "1px solid #0000000F",
            borderRadius: 8,
            padding: 24,
          },
        }}
        options={{
          search: {
            name: "search",
            placeholder: "Search by recording name",
            style: { width: 280 },
          },
        }}
        columnsState={{
          persistenceKey: "pro-table-recordings",
          persistenceType: "localStorage",
        }}
      />
      {/* <Modal
        title={currentVideo?.name || "Video"}
        open={videoPlayerOpen}
        onCancel={() => {
          setVideoPlayerOpen(false);
          setCurrentVideo(null);
        }}
        width={1024}
        footer={false}
        destroyOnClose
        styles={{ container: { background: "#00000055" } }}
      >
        {currentVideo?.fileLink && (
          <video
            controls
            autoPlay
            style={{ width: "100%", borderRadius: 8 }}
            src={currentVideo.fileLink}
          >
            Your browser does not support the video tag.
          </video>
        )}
      </Modal> */}
      <VideoPlayerModal
        open={videoPlayerOpen}
        onClose={() => {
          setVideoPlayerOpen(false);
          setCurrentVideo(null);
        }}
        videoName={currentVideo?.name}
        fileLink={currentVideo?.fileLink}
      />
      <RecordingModal
        open={modalOpen}
        mode={modalMode}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        recordId={editingRecord ? editingRecord.id : undefined}
      />
    </>
  );
}
